import React, { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    setSelectedPoint,
    setSelectedDistrict,
    setFilter,
    setLegendVisibility,
    updatePointDetail,
} from "../../store/pointsSlice";
import { fetchDepositDetail } from "../../services/api";
import { ReactComponent as MapSvg } from "../../data/kartachtonado.svg";
import "./Map.css";
import ObjectLegend from "../ObjectLegend/ObjectLegend";
import { convertRegionName, regionNameMap } from "../../data/regionMapping";
import { getGradientMax, sumCo2ForYear } from "../../utils/co2";
import asbestosIcon from "../../data/icons/asbestos.webp";
import aluminumIcon from "../../data/icons/aluminum.webp";
import apatitesIcon from "../../data/icons/apatites.webp";
import chromeIcon from "../../data/icons/chrome.webp";
import cobaltIcon from "../../data/icons/cobalt.webp";
import copperIcon from "../../data/icons/copper.webp";
import diamondIcon from "../../data/icons/diamond.webp";
import goldIcon from "../../data/icons/gold.webp";
import ironIcon from "../../data/icons/iron.webp";
import magnesiteIcon from "../../data/icons/magnesite.webp";
import manganeseIcon from "../../data/icons/manganese.webp";
import molybdenumIcon from "../../data/icons/molybdenum.webp";
import nickelIcon from "../../data/icons/nickel.webp";
import otherIcon from "../../data/icons/other.png";
import platinumIcon from "../../data/icons/platinum.webp";
import polimetallIcon from "../../data/icons/polimetall.webp";
import silverIcon from "../../data/icons/silver.webp";
import sulfurIcon from "../../data/icons/sulfur.webp";
import tinIcon from "../../data/icons/tin.webp";
import talcumIcon from "../../data/icons/talcum.webp";
import titalIcon from "../../data/icons/tital.webp";
import tungstenIcon from "../../data/icons/tungsten.webp";
import uraniumIcon from "../../data/icons/uranium.webp";

const DENSITY_COLORS = {
    mining: "rgba(255, 166, 0, 0.8)",
    alkaline: "rgba(0, 255, 255, 0.8)",
};

// Калибровочные точки с известными координатами
const calibrationPoints = [
    {
        name: "Москва",
        geo: [55.7558, 37.6176],
        svg: [2769.5, 4689.42],
        region: "Москва",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Новая Москва",
        geo: [55.7558, 37.6176],
        svg: [2769.5, 4689.42],
        region: "Московская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Калининград",
        geo: [54.7104, 20.4522],
        svg: [1375.19, 2420.84],
        region: "Калининградская область",
        federalDistrict: "Северо-Западный",
        capital: true,
    },
    {
        name: "Владивосток",
        geo: [43.1155, 131.8855],
        svg: [17794.73, 10667.99],
        region: "Приморский край",
        federalDistrict: "Дальневосточный",
        capital: true,
    },
    {
        name: "Барнаул",
        geo: [53.36, 83.76],
        svg: [8335.28, 9214.28],
        region: "Алтайский край",
        federalDistrict: "Сибирский",
        capital: true,
    },
    {
        name: "Санкт-Петербург",
        geo: [59.94, 30.31],
        svg: [3329.18, 3000.23],
        region: "Санкт-Петербург",
        federalDistrict: "Северо-Западный",
        capital: true,
    },
    {
        name: "Санкт-Петербург",
        geo: [59.94, 30.31],
        svg: [3329.18, 3000.23],
        region: "Ленинградская область",
        federalDistrict: "Северо-Западный",
        capital: true,
    },
    {
        name: "Томск",
        geo: [56.5, 84.97],
        svg: [9100.9, 8460.34],
        region: "Томская область",
        federalDistrict: "Сибирский",
        capital: true,
    },
    {
        name: "Майкоп",
        geo: [44.61, 40.11],
        svg: [859.9, 7310.34],
        region: "Республика Адыгея",
        federalDistrict: "Южный",
        capital: true,
    },
    {
        name: "Горно-Алтайск",
        geo: [51.9581, 85.9606],
        svg: [9090.69, 9886.27],
        region: "Республика Алтай",
        federalDistrict: "Сибирский",
        capital: true,
    },
    {
        name: "Уфа",
        geo: [54.7388, 55.9721],
        svg: [4725.9, 7100.34],
        region: "Республика Башкортостан",
        federalDistrict: "Уральский",
        capital: true,
    },
    {
        name: "Улан-Удэ",
        geo: [51.8355, 107.5847],
        svg: [12844.24, 10107.25],
        region: "Республика Бурятия",
        federalDistrict: "Сибирский",
        capital: true,
    },
    {
        name: "Махачкала",
        geo: [42.9849, 47.5046],
        svg: [1491.36, 8698.68],
        region: "Республика Дагестан",
        federalDistrict: "Северо-Кавказский",
        capital: true,
    },
    {
        name: "Магас",
        geo: [43.1688, 44.8136],
        svg: [1186.16, 8408.96],
        region: "Республика Ингушетия",
        federalDistrict: "Северо-Кавказский",
        capital: true,
    },
    {
        name: "Нальчик",
        geo: [43.605, 41.26],
        svg: [992.37, 8092.3],
        region: "Кабардино-Балкарская Республика",
        federalDistrict: "Северо-Кавказский",
        capital: true,
    },
    {
        name: "Элиста",
        geo: [46.3083, 44.27],
        svg: [1695.07, 7575.41],
        region: "Республика Калмыкия",
        federalDistrict: "Южный",
        capital: true,
    },
    {
        name: "Черкесск",
        geo: [44.2269, 42.0468],
        svg: [927.21, 7768.77],
        region: "Карачаево-Черкесская Республика",
        federalDistrict: "Северо-Кавказский",
        capital: true,
    },
    {
        name: "Петрозаводск",
        geo: [61.7849, 34.3469],
        svg: [4402.46, 3288.88],
        region: "Республика Карелия",
        federalDistrict: "Северо-Западный",
        capital: true,
    },
    {
        name: "Сыктывкар",
        geo: [61.6689, 50.8357],
        svg: [5441.11, 4913.15],
        region: "Республика Коми",
        federalDistrict: "Северо-Западный",
        capital: true,
    },
    {
        name: "Симферополь",
        geo: [44.9521, 34.1024],
        svg: [146.14, 6323.88],
        region: "Республика Крым",
        federalDistrict: "Южный",
        capital: true,
    },
    {
        name: "Луганск",
        geo: [48.5671, 39.3171],
        svg: [1535.88, 6187.81],
        region: "Луганская область",
        federalDistrict: "Южный",
        capital: true,
    },
    {
        name: "Йошкар-Ола",
        geo: [56.6344, 47.8999],
        svg: [4197.41, 5791.82],
        region: "Республика Марий Эл",
        federalDistrict: "Приволжский",
        capital: true,
    },
    {
        name: "Саранск",
        geo: [54.1808, 45.186],
        svg: [3419.67, 6060.74],
        region: "Республика Мордовия",
        federalDistrict: "Приволжский",
        capital: true,
    },
    {
        name: "Якутск",
        geo: [62.0281, 129.7326],
        svg: [15680.75, 6719.42],
        region: "Республика Саха",
        federalDistrict: "Дальневосточный",
        capital: true,
    },
    {
        name: "Владикавказ",
        geo: [43.0241, 44.6819],
        svg: [1062.76, 8402.68],
        region: "Республика Северная Осетия",
        federalDistrict: "Северо-Кавказский",
        capital: true,
    },
    {
        name: "Казань",
        geo: [55.7903, 49.1347],
        svg: [4029.04, 6102.02],
        region: "Республика Татарстан",
        federalDistrict: "Приволжский",
        capital: true,
    },
    {
        name: "Киров",
        geo: [58.7903, 49.1347],
        svg: [4715.04, 5592.02],
        region: "Кировская область",
        federalDistrict: "Приволжский",
        capital: true,
    },
    {
        name: "Пермь",
        geo: [58.01, 56.1347],
        svg: [5460.04, 6220.02],
        region: "Пермский край",
        federalDistrict: "Уральский",
        capital: true,
    },
    {
        name: "Оренбург",
        geo: [51.77, 55.1],
        svg: [4198.06, 7785.04],
        region: "Оренбургская область",
        federalDistrict: "Приволжский",
        capital: true,
    },
    {
        name: "Челябинск",
        geo: [55.15, 61.34],
        svg: [5506.5, 7495.42],
        region: "Челябинская область",
        federalDistrict: "Уральский",
        capital: true,
    },
    {
        name: "Курган",
        geo: [55.45, 65.34],
        svg: [6009.5, 7617.42],
        region: "Курганская область",
        federalDistrict: "Уральский",
        capital: true,
    },
    {
        name: "Омск",
        geo: [54.99, 73.37],
        svg: [7335.5, 8158.42],
        region: "Омская область",
        federalDistrict: "Сибирский",
        capital: true,
    },
    {
        name: "Петропавловск-Камчатский",
        geo: [53.15, 158.65],
        svg: [20304.5, 5749.42],
        region: "Камчатский край",
        federalDistrict: "Дальневосточный",
        capital: true,
    },
    {
        name: "Анадырь",
        geo: [64.73, 177.51],
        svg: [18664.11, 1633.07],
        region: "Чукотский автономный округ",
        federalDistrict: "Дальневосточный",
        capital: true,
    },
    {
        name: "Магадан",
        geo: [59.56, 150.8],
        svg: [18127.88, 5215.1],
        region: "Магаданская область",
        federalDistrict: "Дальневосточный",
        capital: true,
    },
    {
        name: "Южно-Сахалинск",
        geo: [46.95, 142.74],
        svg: [19344.88, 9086.1],
        region: "Сахалинская область",
        federalDistrict: "Дальневосточный",
        capital: true,
    },
    {
        name: "Хабаровск",
        geo: [48.48, 135.09],
        svg: [17974.88, 9486.1],
        region: "Хабаровский край",
        federalDistrict: "Дальневосточный",
        capital: true,
    },
    {
        name: "Биробиджан",
        geo: [48.79, 132.92],
        svg: [17544.88, 9613.1],
        region: "Еврейская автономная область",
        federalDistrict: "Дальневосточный",
        capital: true,
    },
    {
        name: "Благовещенск",
        geo: [50.29, 127.53],
        svg: [16541.88, 9604.1],
        region: "Амурская область",
        federalDistrict: "Дальневосточный",
        capital: true,
    },
    {
        name: "Чита",
        geo: [52.03, 113.5],
        svg: [14176.88, 9450.1],
        region: "Забайкальский край",
        federalDistrict: "Дальневосточный",
        capital: true,
    },
    {
        name: "Иркутск",
        geo: [52.29, 104.3],
        svg: [12389.88, 9957.1],
        region: "Иркутская область",
        federalDistrict: "Дальне",
        capital: true,
    },
    {
        name: "Кызыл",
        geo: [51.72, 94.44],
        svg: [10525.88, 10073.1],
        region: "Республика Тыва",
        federalDistrict: "Сибирский",
        capital: true,
    },
    {
        name: "Великий Новгород",
        geo: [58.52, 31.27],
        svg: [3105.35, 3491.98],
        region: "Новгородская область",
        federalDistrict: "Северо-Западный",
        capital: true,
    },
    {
        name: "Абакан",
        geo: [53.72, 91.43],
        svg: [10003.88, 9573.1],
        region: "Республика Хакасия",
        federalDistrict: "Сибирский",
        capital: true,
    },
    {
        name: "Кемерово",
        geo: [55.35, 86.09],
        svg: [9327.88, 8902.1],
        region: "Кемеровская область",
        federalDistrict: "Сибирский",
        capital: true,
    },
    {
        name: "Ханты-Мансийск",
        geo: [61.0, 69.0],
        svg: [7374.88, 6671.1],
        region: "Ханты-Мансийский автономный округ",
        federalDistrict: "Уральский",
        capital: true,
    },
    {
        name: "Салехард",
        geo: [66.53, 66.61],
        svg: [7972.88, 5130.1],
        region: "Ямало-Ненецкий автономный округ",
        federalDistrict: "Уральский",
        capital: true,
    },
    {
        name: "Нарьян-Мар",
        geo: [67.64, 53.0],
        svg: [6951.88, 3955.1],
        region: "Ненецкий автономный округ",
        federalDistrict: "Северо-Западный",
        capital: true,
    },
    {
        name: "Архангельск",
        geo: [64.54, 40.52],
        svg: [5293.88, 3468.1],
        region: "Архангельская область",
        federalDistrict: "Северо-Западный",
        capital: true,
    },
    {
        name: "Мурманск",
        geo: [68.96, 33.08],
        svg: [5773.88, 2002.1],
        region: "Мурманская область",
        federalDistrict: "Северо-Западный",
        capital: true,
    },
    {
        name: "Екатеринбург",
        geo: [56.84, 60.6],
        svg: [5778.88, 6753.1],
        region: "Свердловская область",
        federalDistrict: "Уральский",
        capital: true,
    },
    {
        name: "Псков",
        geo: [57.83, 28.33],
        svg: [2609.18, 3252.23],
        region: "Псковская область",
        federalDistrict: "Северо-Западный",
        capital: true,
    },
    {
        name: "Смоленск",
        geo: [54.78, 32.05],
        svg: [2200.04, 4242.02],
        region: "Смоленская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Тверь",
        geo: [56.86, 35.91],
        svg: [3067.04, 4235.02],
        region: "Тверская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Вологда",
        geo: [59.22, 39.89],
        svg: [4005.04, 4444.02],
        region: "Вологодская область",
        federalDistrict: "Северо-Западный",
        capital: true,
    },
    {
        name: "Ярославль",
        geo: [57.62, 39.87],
        svg: [3632.04, 4442.02],
        region: "Ярославская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Кострома",
        geo: [57.8, 40.99],
        svg: [3758.04, 4771.02],
        region: "Костромская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Иваново",
        geo: [57.0, 40.97],
        svg: [3556.04, 4981.02],
        region: "Ивановская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Нижний Новгород",
        geo: [56.3, 43.96],
        svg: [3549.04, 5442.02],
        region: "Нижегородская область",
        federalDistrict: "Приволжский",
        capital: true,
    },
    {
        name: "Брянск",
        geo: [53.25, 34.37],
        svg: [1984.04, 4730.02],
        region: "Брянская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Калуга",
        geo: [54.51, 36.26],
        svg: [2495.04, 4754.02],
        region: "Калужская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Орёл",
        geo: [52.97, 36.07],
        svg: [2139.04, 5169.02],
        region: "Орловская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Курск",
        geo: [51.73, 36.19],
        svg: [1827.04, 5271.02],
        region: "Курская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Белгород",
        geo: [50.6, 36.59],
        svg: [1733.04, 5671.02],
        region: "Белгородская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Липецк",
        geo: [52.61, 39.59],
        svg: [2369.04, 5501.02],
        region: "Липецкая область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Рязань",
        geo: [54.6229, 39.7385],
        svg: [2893.04, 5424.02],
        region: "Рязанская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Тамбов",
        geo: [52.7211, 41.4357],
        svg: [2622.04, 5882.02],
        region: "Тамбовская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Пенза",
        geo: [53.1944, 45.0159],
        svg: [3105.04, 6220.02],
        region: "Пензенская область",
        federalDistrict: "Приволжский",
        capital: true,
    },
    {
        name: "Чебоксары",
        geo: [56.1348, 47.2461],
        svg: [3912.04, 5898.02],
        region: "Чувашская Республика",
        federalDistrict: "Приволжский",
        capital: true,
    },
    {
        name: "Ульяновск",
        geo: [54.3081, 48.3548],
        svg: [3676.04, 6358.02],
        region: "Ульяновская область",
        federalDistrict: "Приволжский",
        capital: true,
    },
    {
        name: "Самара",
        geo: [53.1951, 50.1001],
        svg: [3752.04, 6807.02],
        region: "Самарская область",
        federalDistrict: "Приволжский",
        capital: true,
    },
    {
        name: "Ижевск",
        geo: [56.8526, 53.2111],
        svg: [4806.04, 6178.02],
        region: "Удмуртская Республика",
        federalDistrict: "Приволжский",
        capital: true,
    },
    {
        name: "Саратов",
        geo: [51.5322, 46.0342],
        svg: [3056.04, 6797.02],
        region: "Саратовская область",
        federalDistrict: "Приволжский",
        capital: true,
    },
    {
        name: "Воронеж",
        geo: [51.672, 39.184],
        svg: [2145.04, 5903.02],
        region: "Воронежская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Ростов-на-Дону",
        geo: [47.2357, 39.7015],
        svg: [1265.04, 6726.02],
        region: "Ростовская область",
        federalDistrict: "Южный",
        capital: true,
    },
    {
        name: "Краснодар",
        geo: [45.0355, 38.9753],
        svg: [860.04, 7081.02],
        region: "Краснодарский край",
        federalDistrict: "Южный",
        capital: true,
    },
    {
        name: "Ставрополь",
        geo: [45.0448, 41.9733],
        svg: [1183.04, 7453.02],
        region: "Ставропольский край",
        federalDistrict: "Южный",
        capital: true,
    },
    {
        name: "Астрахань",
        geo: [46.3499, 48.037],
        svg: [2178.04, 8218.02],
        region: "Астраханская область",
        federalDistrict: "Южный",
        capital: true,
    },
    {
        name: "Грозный",
        geo: [43.3178, 45.6981],
        svg: [1369.04, 8514.02],
        region: "Чеченская Республика",
        federalDistrict: "Северо-Кавказский",
        capital: true,
    },
    {
        name: "Донецк",
        geo: [48.0028, 37.8053],
        svg: [1157.04, 6277.02],
        region: "Донецкая область",
        federalDistrict: "Южный",
        capital: true,
    },
    {
        name: "Запорожье",
        geo: [47.8517, 35.1171],
        svg: [652.04, 6088.02],
        region: "Запорожская область",
        federalDistrict: "Южный",
        capital: true,
    },
    {
        name: "Херсон",
        geo: [46.6354, 32.6169],
        svg: [483.04, 5917.02],
        region: "Херсонская область",
        federalDistrict: "Южный",
        capital: true,
    },
    {
        name: "Красноярск",
        geo: [56.0097, 92.7917],
        svg: [10316.88, 9444.1],
        region: "Красноярский край",
        federalDistrict: "Сибирский",
        capital: true,
    },
    {
        name: "Сольцы",
        geo: [58.1206, 30.3092],
        svg: [3455.35, 3591.98],
        region: "Новгородская область",
        federalDistrict: "Северо-Западный",
    },
    {
        name: "Котлас",
        geo: [61.2536, 46.6499],
        svg: [4993.35, 3468.02],
        region: "Архангельская область",
        federalDistrict: "Северо-Западный",
    },
    {
        name: "Владимир",
        geo: [56.1291, 40.4066],
        svg: [3200.04, 4938.02],
        region: "Владимирская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Тюмень",
        geo: [57.1522, 65.5272],
        svg: [6483.04, 7343.02],
        region: "Тюменская область",
        federalDistrict: "Уральский",
    },
    {
        name: "Волгоград",
        geo: [48.708, 44.513],
        svg: [2400.04, 6892.02],
        region: "Волгоградская область",
        federalDistrict: "Южный",
        capital: true,
    },
    {
        name: "Тула",
        geo: [54.1935, 37.616],
        svg: [2568, 5050],
        region: "Тульская область",
        federalDistrict: "Центральный",
        capital: true,
    },
    {
        name: "Новосибирск",
        geo: [55.0084, 82.9357],
        svg: [8600, 8860],
        region: "Новосибирская область",
        federalDistrict: "Сибирский",
        capital: true,
    },
    // доп точки
    {
        name: "Хатанга",
        geo: [71.98, 102.5],
        svg: [11710.94, 4230.72],
        region: "Красноярский край",
        federalDistrict: "Сибирский",
    },
    {
        name: "Норильск",
        geo: [69.35, 88.2],
        svg: [10030.8, 4747.51],
        region: "Красноярский край",
        federalDistrict: "Сибирский",
    },
    {
        name: "Тура",
        geo: [64.3, 100.2],
        svg: [11960, 6503],
        region: "Красноярский край",
        federalDistrict: "Сибирский",
    },
    {
        name: "Тураханск",
        geo: [65.3, 87.935],
        svg: [9970, 6702],
        region: "Красноярский край",
        federalDistrict: "Сибирский",
    },
    {
        name: "Депутатский",
        geo: [69.3, 139.5],
        svg: [15519.94, 3663.72],
        region: "Республика Саха",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Удачный",
        geo: [66.4, 112.9],
        svg: [12961.04, 5663.78],
        region: "Республика Саха",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Алакуртти",
        geo: [67.846944, 37.424444],
        svg: [5205.88, 2117.1],
        region: "Мурманская область",
        federalDistrict: "Северо-Западный",
    },
    {
        name: "Славгород",
        geo: [50.8, 81.9],
        svg: [7945, 9434],
        region: "Алтайский край",
        federalDistrict: "Сибирский",
    },
    {
        name: "Дальнегорск",
        geo: [51.127, 132.868],
        svg: [18530.88, 10166.1],
        region: "Приморский край",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Комсомольск-на-Амуре",
        geo: [48.61, 135.16],
        svg: [17982.54, 8904.06],
        region: "Хабаровский край",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Енисейск",
        geo: [58.45, 92.0],
        svg: [10490.88, 8164.1],
        region: "Красноярский край",
        federalDistrict: "Сибирский",
    },
    {
        name: "Тикси",
        geo: [71.6, 128.9],
        svg: [13788.75, 3889.42],
        region: "Республика Саха",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Черский",
        geo: [68.75, 153.71],
        svg: [16828.75, 2657.42],
        region: "Республика Саха",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Пеледуй",
        geo: [62.8333, 124.7667],
        svg: [13297.94, 7658.15],
        region: "Республика Саха",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Билибино",
        geo: [68.05, 166.4],
        svg: [17561.88, 2183.1],
        region: "Чукотский автономный округ",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Лаврентия",
        geo: [65.0, 171.0],
        svg: [18805.88, 479.1],
        region: "Чукотский автономный округ",
        federalDistrict: "Дальневосточный",
    },

    // 5 точек каждого субъекта РФ дополнительно

    // Республика Алтай
    {
        name: "Кош-Агач",
        geo: [49.9929, 88.6487],
        svg: [9504.93, 10415.07],
        region: "Республика Алтай",
        federalDistrict: "Сибирский",
    },
    {
        name: "Усть-Улаган",
        geo: [50.63258, 87.96046],
        svg: [9298.73, 10274.56],
        region: "Республика Алтай",
        federalDistrict: "Сибирский",
    },
    {
        name: "Турочак",
        geo: [52.2576, 87.1224],
        svg: [9318.14, 9869.07],
        region: "Республика Алтай",
        federalDistrict: "Сибирский",
    },
    {
        name: "Чемал",
        geo: [51.387894, 86.015694],
        svg: [9115.34, 10086.65],
        region: "Республика Алтай",
        federalDistrict: "Сибирский",
    },
    {
        name: "Усть-Кокса",
        geo: [50.2696, 85.6108],
        svg: [8992.6, 10314.35],
        region: "Республика Алтай",
        federalDistrict: "Сибирский",
    },

    //Республика Тыва
    {
        name: "Эрзин",
        geo: [50.260762, 95.162751],
        svg: [10737.75, 10549.17],
        region: "Республика Тыва",
        federalDistrict: "Сибирский",
    },
    {
        name: "Тоора-Хем",
        geo: [52.465636, 96.11961],
        svg: [10993.92, 10017.27],
        region: "Республика Тыва",
        federalDistrict: "Сибирский",
    },
    {
        name: "Кызыл-Мажалык",
        geo: [51.146167, 90.579322],
        svg: [9883.85, 10178.23],
        region: "Республика Тыва",
        federalDistrict: "Сибирский",
    },
    {
        name: "Мугур-Аксы",
        geo: [50.382523, 90.432519],
        svg: [9798.46, 10379.18],
        region: "Республика Тыва",
        federalDistrict: "Сибирский",
    },
    {
        name: "Бай-Хаак",
        geo: [51.241735, 95.55368],
        svg: [10641.68, 10305.46],
        region: "Республика Тыва",
        federalDistrict: "Сибирский",
    },

    // Республика Хакасия
    {
        name: "Аскиз",
        geo: [53.13194, 90.52639],
        svg: [9910.53, 9689.28],
        region: "Республика Хакасия",
        federalDistrict: "Сибирский",
    },
    {
        name: "Копьево",
        geo: [54.9886079, 89.8333391],
        svg: [9866.05, 9166.92],
        region: "Республика Хакасия",
        federalDistrict: "Сибирский",
    },
    {
        name: "Таштып",
        geo: [52.79542, 89.89477],
        svg: [9755.77, 9777.11],
        region: "Республика Хакасия",
        federalDistrict: "Сибирский",
    },
    {
        name: "Нижняя точка Хакасии(Бедуй)",
        geo: [51.72306, 88.50611],
        svg: [9584.99, 9921.21],
        region: "Республика Хакасия",
        federalDistrict: "Сибирский",
    },
    {
        name: "Шира",
        geo: [54.487191, 89.962817],
        svg: [9878.51, 9372.96],
        region: "Республика Хакасия",
    },

    // Иркутская область
    {
        name: "Бодайбо",
        geo: [57.85, 114.9],
        svg: [13735.37, 8089.66],
        region: "Иркутская область",
        federalDistrict: "Сибирский",
    },
    {
        name: "Усть-Илимск",
        geo: [57.943039, 102.741523],
        svg: [12004.41, 8252.51],
        region: "Иркутская область",
        federalDistrict: "Сибирский",
    },
    {
        name: "Киренск",
        geo: [57.78528, 108.11194],
        svg: [12836.32, 8202.6],
        region: "Иркутская область",
        federalDistrict: "Сибирский",
    },
    {
        name: "Светлый",
        geo: [58.436142, 115.964427],
        svg: [14017.63, 7961.35],
        region: "Иркутская область",
        federalDistrict: "Сибирский",
    },
    {
        name: "Тайшет",
        geo: [55.9342, 98.0044],
        svg: [11243.63, 8909.72],
        region: "Иркутская область",
        federalDistrict: "Сибирский",
    },
    {
        name: "Зима",
        geo: [53.533333, 102.033333],
        svg: [11829.71, 9317.35],
        region: "Иркутская область",
        federalDistrict: "Сибирский",
    },

    // Республика Бурятия
    {
        name: "Хужир",
        geo: [53.193456, 107.343646],
        svg: [11521.91, 9838.46],
        region: "Республика Бурятия",
        federalDistrict: "Сибирский",
    },
    {
        name: "Северомуйск",
        geo: [56.14806, 113.42667],
        svg: [13759.74, 8651.83],
        region: "Республика Бурятия",
        federalDistrict: "Сибирский",
    },
    {
        name: "Кяхта",
        geo: [50.33333, 106.5],
        svg: [12819.68, 10440.43],
        region: "Республика Бурятия",
        federalDistrict: "Сибирский",
    },
    {
        name: "Баянгол",
        geo: [50.700506, 103.469356],
        svg: [12245.67, 10392.43],
        region: "Республика Бурятия",
        federalDistrict: "Сибирский",
    },
    {
        name: "Баргузин",
        geo: [53.5962, 109.6314],
        svg: [13069.25, 9480.95],
        region: "Республика Бурятия",
        federalDistrict: "Сибирский",
    },
    {
        name: "Ильчирское",
        geo: [51.8667, 106.1667],
        svg: [12420.88, 10390.1],
        region: "Республика Бурятия",
        federalDistrict: "Сибирский",
    },

    // Забайкальский край
    {
        name: "Новая Чара",
        geo: [56.6687, 118.167],
        svg: [14525.09, 8453.5],
        region: "Забайкальский край",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Могоча",
        geo: [53.7417, 119.75752],
        svg: [14907.77, 9130.5],
        region: "Забайкальский край",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Забайкальск",
        geo: [49.6551, 117.3227],
        svg: [14733.07, 10174.93],
        region: "Забайкальский край",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Петровск-Забайкальский",
        geo: [51.27312, 115.0],
        svg: [14220.58, 9893.5],
        region: "Забайкальский край",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Хапчеранга",
        geo: [49.70139, 112.38833],
        svg: [13769.1, 10444.88],
        region: "Забайкальский край",
        federalDistrict: "Дальневосточный",
    },

    //Еврейская автономная область
    {
        name: "Амурзет",
        geo: [48.7833, 132.9167],
        svg: [17378.75, 10006.71],
        region: "Еврейская автономная область",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Облучье",
        geo: [49.2667, 132.1667],
        svg: [17312.13, 9615.76],
        region: "Еврейская автономная область",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Ленинское",
        geo: [49.4167, 133.1667],
        svg: [17592.03, 9824.18],
        region: "Еврейская автономная область",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Теплоозерск",
        geo: [48.9667, 133.5],
        svg: [17395.23, 9615.4],
        region: "Еврейская автономная область",
        federalDistrict: "Дальневосточный",
    },
    {
        name: "Смидович",
        geo: [48.5833, 133.1667],
        svg: [17693.42, 9598.29],
        region: "Еврейская автономная область",
        federalDistrict: "Дальневосточный",
    },

    // Ханты-Мансийский автономный округ
    {
        name: "Белоярский",
        geo: [63.7166, 66.666],
        svg: [7535.65, 5769.19],
        region: "Ханты-Мансийский автономный округ",
        federalDistrict: "Уральский",
    },
    {
        name: "Радужный",
        geo: [62.09611, 77.475],
        svg: [8686.82, 6694.73],
        region: "Ханты-Мансийский автономный округ",
        federalDistrict: "Уральский",
    },
    {
        name: "Лангепас",
        geo: [61.27032, 75.17009],
        svg: [8278.6, 6817.19],
        region: "Ханты-Мансийский автономный округ",
        federalDistrict: "Уральский",
    },
    {
        name: "Комсомольский",
        geo: [62.262662, 66.995465],
        svg: [6768.21, 6161.31],
        region: "Ханты-Мансийский автономный округ",
        federalDistrict: "Уральский",
    },
    {
        name: "Лянторский",
        geo: [61.62131, 72.16452],
        svg: [7886.71, 6547.77],
        region: "Ханты-Мансийский автономный округ",
        federalDistrict: "Уральский",
    },

    // Свердловская область
    {
        name: "Ивдель",
        geo: [60.683333, 60.433333],
        svg: [6335.5, 5984.43],
        region: "Свердловская область",
        federalDistrict: "Уральский",
    },
    {
        name: "Тавда",
        geo: [58.05, 65.266667],
        svg: [6408.98, 6968.55],
        region: "Свердловская область",
        federalDistrict: "Уральский",
    },
    {
        name: "Красноуфимск",
        geo: [56.616667, 57.766667],
        svg: [5339.45, 6802.27],
        region: "Свердловская область",
        federalDistrict: "Уральский",
    },
    {
        name: "Алапаевск",
        geo: [57.85, 61.7],
        svg: [6155.88, 6604.92],
        region: "Свердловская область",
        federalDistrict: "Уральский",
    },
    {
        name: "Гари",
        geo: [59.43075, 62.34967],
        svg: [6384.48, 6271.54],
        region: "Свердловская область",
        federalDistrict: "Уральский",
    },
    {
        name: "Нижний Тагил",
        geo: [57.916667, 59.983333],
        svg: [5927.28, 6519.61],
        region: "Свердловская область",
        federalDistrict: "Уральский",
    },
    {
        name: "Верхотурье",
        geo: [58.86416, 58.86416],
        svg: [6164.05, 6328.36],
        region: "Свердловская область",
        federalDistrict: "Уральский",
    },

    // Пермский край
    {
        name: "Гайны",
        geo: [60.298656, 54.341265],
        svg: [5568.05, 5576.22],
        region: "Пермский край",
        federalDistrict: "Уральский",
    },
    {
        name: "Ныроб",
        geo: [60.73332, 56.719295],
        svg: [5994.24, 5692.53],
        region: "Пермский край",
        federalDistrict: "Уральский",
    },
    {
        name: "Чернушка",
        geo: [56.50722, 56.07661],
        svg: [5151.67, 6641.98],
        region: "Пермский край",
        federalDistrict: "Уральский",
    },
    {
        name: "Чайковский",
        geo: [56.7733, 54.1403],
        svg: [4972.1, 6457.96],
        region: "Пермский край",
        federalDistrict: "Уральский",
    },
    {
        name: "Кудымкар",
        geo: [59.016667, 54.666667],
        svg: [5339.45, 5960.88],
        region: "Пермский край",
        federalDistrict: "Уральский",
    },
    {
        name: "Чусовой",
        geo: [58.2833, 57.8167],
        svg: [5666.02, 6413.38],
        region: "Пермский край",
        federalDistrict: "Уральский",
    },
    {
        name: "Березники",
        geo: [59.397778, 56.769167],
        svg: [5715.7, 5999.24],
        region: "Пермский край",
        federalDistrict: "Уральский",
    },
    {
        name: "Кунгур",
        geo: [57.4368, 56.959],
        svg: [5372.11, 6487.88],
        region: "Пермский край",
        federalDistrict: "Уральский",
    },

    {
        name: "Севастополь",
        geo: [44.616667, 33.533333],
        svg: [-3.5, 6319.96],
        region: "Севастополь",
        federalDistrict: "Южный",
    },
];

const toRadians = (deg) => (deg * Math.PI) / 180;

const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);

    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRadians(lat1)) *
            Math.cos(toRadians(lat2)) *
            Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

const calibrateCoordinates = (lat, lon, region, federalDistrict) => {
    const sameRegionPoints = calibrationPoints.filter(
        (point) => point.region === region,
    );

    // Взвешенное усреднение допускаем только между калибровочными точками
    // ОДНОГО И ТОГО ЖЕ региона. Раньше при < 2 своих точек в ход шли точки
    // соседних регионов всего федерального округа, и для регионов на краю
    // округа (например, Башкортостан на границе с Уралом) результат
    // систематически утягивало к центру округа, за пределы самого региона.
    if (sameRegionPoints.length >= 2) {
        const nearestPoints = sameRegionPoints
            .map((p) => ({
                point: p,
                distance: haversineDistance(lat, lon, p.geo[0], p.geo[1]),
            }))
            .sort((a, b) => a.distance - b.distance)
            .slice(0, 3);

        const weights = nearestPoints.map(
            ({ distance }) => 1 / (distance + 0.1),
        );
        const weightSum = weights.reduce((a, b) => a + b, 0);
        const normalizedWeights = weights.map((w) => w / weightSum);

        const svgX = nearestPoints.reduce(
            (sum, { point }, i) => sum + point.svg[0] * normalizedWeights[i],
            0,
        );
        const svgY = nearestPoints.reduce(
            (sum, { point }, i) => sum + point.svg[1] * normalizedWeights[i],
            0,
        );

        return [svgX, svgY];
    }

    // Меньше 2 точек в самом регионе: берём ближайшую доступную калибровочную
    // точку (свою, а если своих вовсе нет — из федерального округа, затем
    // любую) и переносим позицию локальным аффинным сдвигом, не смешивая
    // её со свг-координатами точек из других регионов.
    let referencePoints = sameRegionPoints;
    if (referencePoints.length === 0) {
        referencePoints = calibrationPoints.filter(
            (point) => point.federalDistrict === federalDistrict,
        );
    }
    if (referencePoints.length === 0) {
        referencePoints = calibrationPoints;
    }

    const nearest = referencePoints
        .map((p) => ({
            point: p,
            distance: haversineDistance(lat, lon, p.geo[0], p.geo[1]),
        }))
        .sort((a, b) => a.distance - b.distance)[0].point;

    const latScale =
        (nearest.svg[1] / nearest.geo[0]) * Math.cos(toRadians(nearest.geo[0]));
    const lonScale =
        (nearest.svg[0] / nearest.geo[1]) * Math.cos(toRadians(lat));

    const latOffset = (lat - nearest.geo[0]) * latScale;
    const lonOffset = (lon - nearest.geo[1]) * lonScale;

    return [nearest.svg[0] + lonOffset, nearest.svg[1] + latOffset];
};

// Точка-якорь на регион: столица (если есть) или первая калибровочная точка.
// Она гарантированно лежит внутри своего региона на SVG-карте и используется,
// чтобы "притянуть" точки, которые calibrateCoordinates ошибочно вынесла
// за пределы региона (типично для регионов с < 2 калибровочными точками
// на краю федерального округа, где взвешенное усреднение тянет точку
// в сторону соседних регионов).
const regionAnchors = (() => {
    const byRegion = new Map();
    calibrationPoints.forEach((point) => {
        const existing = byRegion.get(point.region);
        if (!existing || (point.capital && !existing.capital)) {
            byRegion.set(point.region, point);
        }
    });
    const anchors = new Map();
    byRegion.forEach((point, region) => {
        anchors.set(convertRegionName(region), point.svg);
    });
    return anchors;
})();

export const highlightDistrict = (district) => {
    const mapElement = document.querySelector(".map-container svg");
    if (!mapElement) return;

    const paths = mapElement.getElementsByTagName("path");

    Array.from(paths).forEach((path) => {
        path.style.fill = "#D3D3D3";
    });

    if (district) {
        const districtRegions = calibrationPoints
            .filter((point) => point.federalDistrict === district)
            .map((point) => point.region);

        Array.from(paths).forEach((path) => {
            const pathTitle = path.getAttribute("title") || path.id;
            const russianRegionName = Object.entries(regionNameMap).find(
                ([_, svgName]) => svgName === pathTitle,
            )?.[0];

            if (
                russianRegionName &&
                districtRegions.includes(russianRegionName)
            ) {
                path.style.fill = "#4B96DC";
            }
        });
    }
};


const SvgMap = ({ setShowLeftPanel }) => {
    const dispatch = useDispatch();
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const mapRef = useRef(null);
    const pathCacheRef = useRef(null);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [defaultColors, setDefaultColors] = useState(new Map());
    const [iconSize, setIconSize] = useState({ width: 200, height: 200 });
    const [showRegionPoints, setShowRegionPoints] = useState(null);
    const [hiddenCapital, setHiddenCapital] = useState(null);
    // const [showLeftPanel, setShowLeftPanel] = useState(window.innerWidth > 768);
    const [showRightPanel, setShowRightPanel] = useState(
        window.innerWidth > 768,
    );

    const points = useSelector((state) => state.points.points || []);
    const selectedGroups = useSelector(
        (state) => state.points.selectedWasteGroups || [],
    );
    const filters = useSelector((state) => state.points.filters);
    const selectedPoint = useSelector((state) => state.points.selectedPoint);
    const selectedRegionFromRedux = useSelector(
        (state) => state.points.selectedRegion,
    );
    const showGradient = useSelector(
        (state) => state.points.showGradientColoring,
    );
    const co2Data = useSelector((state) => state.points.co2Data);
    const showDensityMap = useSelector((state) => state.points.showDensityMap);
    const selectedYear = useSelector(
        (state) => state.points.selectedYear || "2030",
    );
    const MAX_SCALE = 4;
    const MIN_SCALE = 0.5;
    const ZOOM_STEP = 0.2;

    const DRAG_SPEED = 5;

    const BASE_ICON_SIZE = window.innerWidth <= 768 ? 400 : 200;
    const MIN_ICON_SIZE = window.innerWidth <= 768 ? 150 : 75;

    const handleMouseDown = useCallback((e) => {
        setIsDragging(true);
        setDragStart({
            x: e.clientX,
            y: e.clientY,
        });
    }, []);

    const handleMouseMove = useCallback(
        (e) => {
            if (!isDragging) return;

            const dx = (e.clientX - dragStart.x) * DRAG_SPEED;
            const dy = (e.clientY - dragStart.y) * DRAG_SPEED;

            setPosition((prevPosition) => ({
                x: prevPosition.x + dx,
                y: prevPosition.y + dy,
            }));

            setDragStart({
                x: e.clientX,
                y: e.clientY,
            });
        },
        [isDragging, dragStart],
    );

    const handleMouseUp = useCallback(
        (e) => {
            const wasDragging = isDragging;
            setIsDragging(false);
            if (wasDragging) {
                e.stopPropagation();
            }
        },
        [isDragging],
    );

    const handleZoomIn = useCallback(() => {
        const newScale = Math.min(scale + ZOOM_STEP, MAX_SCALE);
        const svgWidth = 20955;
        const svgHeight = 11530;
        const centerX = svgWidth / 2;
        const centerY = svgHeight / 2;

        const scaleFactor = newScale / scale;
        const newX = centerX - (centerX - position.x) * scaleFactor;
        const newY = centerY - (centerY - position.y) * scaleFactor;

        setScale(newScale);
        setPosition({ x: newX, y: newY });
    }, [scale, position]);

    const handleZoomOut = useCallback(() => {
        const newScale = Math.max(scale - ZOOM_STEP, MIN_SCALE);
        const svgWidth = 20955;
        const svgHeight = 11530;
        const centerX = svgWidth / 2;
        const centerY = svgHeight / 2;

        const scaleFactor = newScale / scale;
        const newX = centerX - (centerX - position.x) * scaleFactor;
        const newY = centerY - (centerY - position.y) * scaleFactor;

        setScale(newScale);
        setPosition({ x: newX, y: newY });
    }, [scale, position]);

    const handleReset = useCallback(() => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
        dispatch(
            setFilter({
                federalDistrict: "all",
                region: "all",
                group: "all",
                wasteType: "all",
            }),
        );
        dispatch(setSelectedPoint(null));
        dispatch(setSelectedDistrict(null));
    }, [dispatch]);

    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === "Escape") {
                handleReset();
            }
        };

        window.addEventListener("keydown", handleEscKey);
        return () => window.removeEventListener("keydown", handleEscKey);
    }, [handleReset]);

    const getPathByTitle = useCallback((svgElement, title) => {
        if (!svgElement || !title) return null;
        if (pathCacheRef.current?.svg !== svgElement) {
            const map = new Map();
            Array.from(svgElement.getElementsByTagName("path")).forEach((p) => {
                const t = p.getAttribute("title") || p.id;
                if (t) map.set(t, p);
            });
            pathCacheRef.current = { svg: svgElement, map };
        }
        return pathCacheRef.current.map.get(title) || null;
    }, []);

    // Если calibrateCoordinates промахнулась мимо своего региона (см.
    // regionAnchors выше), подтягиваем точку к ближайшей позиции на отрезке
    // до якоря региона, которая уже лежит внутри его контура на карте.
    const snapPointInsideRegion = useCallback(
        (svgElement, path, x, y, anchor) => {
            if (
                !svgElement ||
                !path ||
                typeof path.isPointInFill !== "function"
            ) {
                return [x, y];
            }

            const pt = svgElement.createSVGPoint();
            const isInside = (px, py) => {
                pt.x = px;
                pt.y = py;
                return path.isPointInFill(pt);
            };

            if (isInside(x, y)) return [x, y];
            if (!anchor || !isInside(anchor[0], anchor[1])) return [x, y];

            let lo = 0;
            let hi = 1;
            for (let i = 0; i < 20; i++) {
                const t = (lo + hi) / 2;
                const px = x + (anchor[0] - x) * t;
                const py = y + (anchor[1] - y) * t;
                if (isInside(px, py)) {
                    hi = t;
                } else {
                    lo = t;
                }
            }

            return [x + (anchor[0] - x) * hi, y + (anchor[1] - y) * hi];
        },
        [],
    );

    const isPointInsideRegion = useCallback(
        (region, x, y) => {
            const svgElement = mapRef.current?.querySelector("svg");
            const svgTitle = convertRegionName(region);
            const path = getPathByTitle(svgElement, svgTitle);
            if (
                !svgElement ||
                !path ||
                typeof path.isPointInFill !== "function"
            ) {
                return true;
            }
            const pt = svgElement.createSVGPoint();
            pt.x = x;
            pt.y = y;
            return path.isPointInFill(pt);
        },
        [getPathByTitle],
    );

    const geoToSvg = useCallback(
        (lat, lon, region, federalDistrict) => {
            const [x, y] = calibrateCoordinates(
                lat,
                lon,
                region,
                federalDistrict,
            );

            const svgElement = mapRef.current?.querySelector("svg");
            const svgTitle = convertRegionName(region);
            const path = getPathByTitle(svgElement, svgTitle);
            const anchor = regionAnchors.get(svgTitle);

            return snapPointInsideRegion(svgElement, path, x, y, anchor);
        },
        [getPathByTitle, snapPointInsideRegion],
    );

    const filteredPoints = React.useMemo(() => {
        return points.filter((point) => {
            const wasteTypeMatch =
                filters.wasteType === "all" ||
                point.wasteType === filters.wasteType;
            const groupMatch =
                filters.group === "all" || point.group === filters.group;
            const districtMatch =
                filters.federalDistrict === "all" ||
                point.federalDistrict === filters.federalDistrict;
            const regionMatch =
                filters.region === "all" || point.region === filters.region;

            return wasteTypeMatch && groupMatch && districtMatch && regionMatch;
        });
    }, [points, filters]);

    const highlightRegion = useCallback(
        (region) => {
            if (!mapRef.current) return;

            const svgElement = mapRef.current.querySelector("svg");
            if (!svgElement) return;

            const paths = svgElement.getElementsByTagName("path");

            Array.from(paths).forEach((path) => {
                path.style.fill = defaultColors.get(path) || "#D3D3D3";
            });

            if (region) {
                const svgRegionName = convertRegionName(region);
                // console.log('Looking for region:', region, '→',
                Array.from(paths).forEach((path) => {
                    const pathRegion = path.getAttribute("title") || path.id;
                    // console.log('Checking path:', pathRegion);
                    if (pathRegion === svgRegionName) {
                        path.style.fill = "#4B96DC";
                    }
                });
            }
        },
        [defaultColors],
    );

    useEffect(() => {
        const svgElement = mapRef.current?.querySelector("svg");
        if (!svgElement) return;

        const paths = svgElement.getElementsByTagName("path");
        const colors = new Map();

        Array.from(paths).forEach((path) => {
            colors.set(path, path.getAttribute("fill") || "#D3D3D3");
            path.style.transition = "fill 0.3s ease";
        });

        setDefaultColors(colors);
    }, []);

    const zoomToRegion = useCallback((regionName) => {
        const svgElement = mapRef.current?.querySelector("svg");
        if (!svgElement) return;

        const paths = svgElement.getElementsByTagName("path");
        const svgRegionName = convertRegionName(regionName);

        const regionPath = Array.from(paths).find((path) => {
            const pathTitle = path.getAttribute("title");
            const pathId = path.id;
            return pathTitle === svgRegionName || pathId === svgRegionName;
        });

        if (regionPath) {
            const bbox = regionPath.getBBox();
            const svgWidth = 20955;
            const svgHeight = 11530;

            const scaleFactor = Math.min(
                svgWidth / (bbox.width * 2),
                svgHeight / (bbox.height * 2),
            );

            const newScale = Math.min(MAX_SCALE, scaleFactor);
            const x = -(
                bbox.x * newScale +
                (bbox.width * newScale) / 2 -
                svgWidth / 2
            );
            const y = -(
                bbox.y * newScale +
                (bbox.height * newScale) / 2 -
                svgHeight / 2
            );

            setScale(newScale);
            setPosition({ x, y });
        }
    }, []);

    useEffect(() => {
        const svgElement = mapRef.current?.querySelector("svg");
        if (!svgElement) return;

        const paths = svgElement.getElementsByTagName("path");

        Array.from(paths).forEach((path) => {
            path.style.fill = defaultColors.get(path) || "#D3D3D3";
        });

        if (filters.region && filters.region !== "all") {
            const svgRegionName = convertRegionName(filters.region);

            Array.from(paths).forEach((path) => {
                const pathTitle = path.getAttribute("title");
                const pathId = path.id;

                if (pathTitle === svgRegionName || pathId === svgRegionName) {
                    path.style.fill = "#4B96DC";
                    zoomToRegion(filters.region);
                }
            });
        } else {
            setScale(1);
            setPosition({ x: 0, y: 0 });
        }
    }, [filters.region, defaultColors, zoomToRegion]);

    useEffect(() => {
        if (selectedPoint) {
            highlightRegion(selectedPoint.region);
            setSelectedRegion(selectedPoint.region);
        } else {
            highlightRegion(null);
            setSelectedRegion(null);
        }
    }, [selectedPoint, highlightRegion]);

    useEffect(() => {
        if (selectedRegionFromRedux) {
            highlightRegion(selectedRegionFromRedux);
            setSelectedRegion(selectedRegionFromRedux);
            zoomToRegion(selectedRegionFromRedux);
        } else if (!selectedPoint && filters.region === "all") {
            highlightRegion(null);
            setSelectedRegion(null);
            setScale(1);
            setPosition({ x: 0, y: 0 });
        }
    }, [
        selectedRegionFromRedux,
        selectedPoint,
        filters.region,
        highlightRegion,
        zoomToRegion,
    ]);

    const handlePointClick = useCallback(
        (point) => {
            dispatch(setSelectedPoint(point));
            dispatch(setLegendVisibility(true));

            // highlightRegion(point.region);
            // setSelectedRegion(point.region);

            const hasComposition =
                (point.chemicalComposition &&
                    Object.keys(point.chemicalComposition).length > 0) ||
                (point.mineralComposition &&
                    Object.keys(point.mineralComposition).length > 0);

            if (!hasComposition && point.id) {
                fetchDepositDetail(point.id)
                    .then((detail) => dispatch(updatePointDetail(detail)))
                    .catch(() => {});
            }
        },
        [dispatch],
    );

    const logAllRegions = useCallback(() => {
        const svgElement = mapRef.current?.querySelector("svg");
        if (!svgElement) return;

        const paths = svgElement.getElementsByTagName("path");
        const regions = new Set();

        Array.from(paths).forEach((path) => {
            const pathTitle = path.getAttribute("title");
            const pathId = path.id;
            const pathRegion = pathTitle || pathId;
            if (pathRegion) {
                regions.add(pathRegion);
            }
        });

        // console.log('All available regions:', Array.from(regions));
    }, []);

    useEffect(() => {
        logAllRegions();
    }, [logAllRegions]);

    const getIconByWasteType = (wasteType) => {
        // Только то, что есть в легенде (MineralsLegend); остальное — «Прочее».
        const icons = {
            алюминий: aluminumIcon,
            апатиты: apatitesIcon,
            асбест: asbestosIcon,
            хром: chromeIcon,
            кобальт: cobaltIcon,
            медь: copperIcon,
            алмаз: diamondIcon,
            золото: goldIcon,
            железо: ironIcon,
            марганец: manganeseIcon,
            магнезит: magnesiteIcon,
            молибден: molybdenumIcon,
            никель: nickelIcon,
            платина: platinumIcon,
            полиметаллы: polimetallIcon,
            серебро: silverIcon,
            сера: sulfurIcon,
            тальк: talcumIcon,
            олово: tinIcon,
            титан: titalIcon,
            вольфрам: tungstenIcon,
            уран: uraniumIcon,
        };
        // Названия из данных, которые отличаются от легенды.
        const aliases = [
            [/^алюминиевое сырье/, "алюминий"],
            [/^апатитовые руды/, "апатиты"],
            [/^железные руды/, "железо"],
            [/^марганцевые руды/, "марганец"],
            [/^хромовые руды/, "хром"],
            [/^платиноиды/, "платина"],
        ];

        const name = (wasteType || "").toLowerCase().replace(/ё/g, "е").trim();
        const alias = aliases.find(([re]) => re.test(name));
        return icons[alias ? alias[1] : name] || otherIcon;
    };

    const getRegionDepositsCount = useCallback(
        (region) => {
            return points.filter((point) => {
                const groupMatch =
                    filters.group === "all" || point.group === filters.group;
                const wasteTypeMatch =
                    filters.wasteType === "all" ||
                    point.wasteType === filters.wasteType;
                const districtMatch =
                    filters.federalDistrict === "all" ||
                    point.federalDistrict === filters.federalDistrict;

                return (
                    point.region === region &&
                    groupMatch &&
                    wasteTypeMatch &&
                    districtMatch
                );
            }).length;
        },
        [points, filters],
    );

    const checkOverlap = (x1, y1, x2, y2, minDistance = 250) => {
        const distance = Math.sqrt(Math.pow(x1 - x2, 2) + Math.pow(y1 - y2, 2));
        return distance < minDistance;
    };

    const updateRegionFilter = useCallback(
        (region) => {
            highlightRegion(region);

            if (region) {
                zoomToRegion(region);
            } else {
                setScale(1);
                setPosition({ x: 0, y: 0 });
            }

            dispatch(setFilter({ region: region || "all" }));
        },
        [dispatch, highlightRegion, zoomToRegion],
    );

    const handleMapClick = useCallback(
        (e) => {
            if (isDragging) {
                return;
            }
            e.stopPropagation();
            const path = e.target;

            if (path.tagName !== "path") return;

            const svgRegionName = path.getAttribute("title") || path.id;
            if (!svgRegionName) return;

            const russianRegionName = Object.entries(regionNameMap).find(
                ([_, svgName]) => svgName === svgRegionName,
            )?.[0];

            if (!russianRegionName) return;

            const filterAction = {
                type: "points/setFilter",
                payload: {
                    ...filters,
                    region:
                        filters.region === russianRegionName
                            ? "all"
                            : russianRegionName,
                    fromBottomPanel: false,
                },
            };

            const regionAction = {
                type: "points/setSelectedRegion",
                payload:
                    filters.region === russianRegionName
                        ? null
                        : russianRegionName,
            };

            dispatch(filterAction);
            dispatch(regionAction);

            if (filters.region !== russianRegionName) {
                highlightRegion(russianRegionName);
                zoomToRegion(russianRegionName);
            }
        },
        [filters.region, dispatch, highlightRegion, zoomToRegion],
    );

    const handleCapitalClick = useCallback(
        (capitalPoint) => {
            const filterAction = {
                type: "points/setFilter",
                payload: {
                    ...filters,
                    region:
                        filters.region === capitalPoint.region
                            ? "all"
                            : capitalPoint.region,
                    fromBottomPanel: false,
                },
            };

            const regionAction = {
                type: "points/setSelectedRegion",
                payload:
                    filters.region === capitalPoint.region
                        ? null
                        : capitalPoint.region,
            };

            dispatch(filterAction);
            dispatch(regionAction);

            if (filters.region !== capitalPoint.region) {
                highlightRegion(capitalPoint.region);
                zoomToRegion(capitalPoint.region);
            }
        },
        [filters.region, dispatch, highlightRegion, zoomToRegion],
    );

    useEffect(() => {
        setShowRegionPoints(filters.region === "all" ? null : filters.region);
        setHiddenCapital(filters.region === "all" ? null : filters.region);
    }, [filters.region]);

    const handleWheel = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();

            const delta = -Math.sign(e.deltaY) * ZOOM_STEP;
            const newScale = Math.min(
                Math.max(scale + delta, MIN_SCALE),
                MAX_SCALE,
            );

            const svgWidth = 20955;
            const svgHeight = 11530;
            const centerX = svgWidth / 2;
            const centerY = svgHeight / 2;

            const scaleFactor = newScale / scale;
            const newX = centerX - (centerX - position.x) * scaleFactor;
            const newY = centerY - (centerY - position.y) * scaleFactor;

            setScale(newScale);
            setPosition({ x: newX, y: newY });
        },
        [scale, position],
    );

    useEffect(() => {
        const mapContainer = mapRef.current;
        if (!mapContainer) return;

        const preventScroll = (e) => {
            e.preventDefault();
            return false;
        };

        mapContainer.addEventListener("wheel", preventScroll, {
            passive: false,
        });

        return () => {
            mapContainer.removeEventListener("wheel", preventScroll);
        };
    }, []);

    const getColorByAbsorption = useCallback((absorption, maxAbsorption) => {
        if (!absorption) return "#F5F5F5";

        // Поглощение по регионам распределено очень неравномерно (один
        // регион может на порядок превосходить остальные), поэтому чисто
        // линейное отношение к максимуму красит средние и мелкие регионы
        // одинаково. Степенная шкала растягивает нижнюю часть диапазона.
        // Логарифм log(x)/log(max) для этого не годится: он зависит от
        // единиц измерения, и при росте всех значений (данные умножили на
        // 100) отношения прижимаются к 1 — разница между 2030 и 2100 годом
        // почти пропадает. (x/max)^k от масштаба не зависит.
        const MIN_INTENSITY = 0.12;
        const GAMMA = 0.3;
        const ratio = Math.pow(absorption / maxAbsorption, GAMMA);
        const intensity = Math.max(MIN_INTENSITY, Math.min(ratio, 1));

        const red = 255;
        const green = Math.round(255 - intensity * 255);
        const blue = Math.round(255 - intensity * 255);

        return `rgb(${red}, ${green}, ${blue})`;
    }, []);

    useEffect(() => {
        if (!mapRef.current) return;

        const svgElement = mapRef.current.querySelector("svg");
        if (!svgElement) return;

        const paths = svgElement.getElementsByTagName("path");

        if (!showGradient || !points) {
            Array.from(paths).forEach((path) => {
                path.style.fill = defaultColors.get(path) || "#D3D3D3";

                if (selectedRegionFromRedux) {
                    const svgRegionName = convertRegionName(
                        selectedRegionFromRedux,
                    );
                    const pathTitle = path.getAttribute("title");
                    const pathId = path.id;

                    if (
                        pathTitle === svgRegionName ||
                        pathId === svgRegionName
                    ) {
                        path.style.fill = "#4B96DC";
                    }
                }
            });
            return;
        }

        const absorptionValues = new Map();

        Array.from(paths).forEach((path) => {
            const svgRegionName = path.getAttribute("title") || path.id;
            const russianRegionName = Object.entries(regionNameMap).find(
                ([_, svgName]) => svgName === svgRegionName,
            )?.[0];

            if (russianRegionName) {
                const regionPoints = points.filter(
                    (point) => point.region === russianRegionName,
                );

                const regionCO2 = sumCo2ForYear(regionPoints, selectedYear);

                if (regionCO2 > 0) {
                    absorptionValues.set(path, regionCO2);
                }
            }
        });

        const maxAbsorption = getGradientMax(points);

        Array.from(paths).forEach((path) => {
            const absorption = absorptionValues.get(path) || 0;
            const color =
                absorption > 0
                    ? getColorByAbsorption(absorption, maxAbsorption)
                    : "#F5F5F5";

            path.style.fill = color;
            path.style.transition = "fill 0.3s ease";
        });
    }, [
        showGradient,
        points,
        selectedYear,
        regionNameMap,
        getColorByAbsorption,
        defaultColors,
        selectedRegionFromRedux,
    ]);

    const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });

    const handleTouchStart = useCallback((e) => {
        setIsDragging(true);
        setTouchStart({
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
        });
    }, []);

    const handleTouchMove = useCallback(
        (e) => {
            if (!isDragging) return;

            const dx = (e.touches[0].clientX - touchStart.x) * DRAG_SPEED;
            const dy = (e.touches[0].clientY - touchStart.y) * DRAG_SPEED;

            setPosition((prevPosition) => ({
                x: prevPosition.x + dx,
                y: prevPosition.y + dy,
            }));

            setTouchStart({
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
            });
        },
        [isDragging, touchStart],
    );

    const handleTouchEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    return (
        <div className="map-container">
            <svg
                ref={mapRef}
                viewBox="0 0 20955 11530"
                preserveAspectRatio="xMidYMid meet"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onClick={handleMapClick}
                onWheel={handleWheel}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
                style={{ cursor: isDragging ? "grabbing" : "grab" }}
            >
                <defs>
                    <clipPath id="circleClip">
                        <circle cx="50%" cy="50%" r="50%" />
                    </clipPath>
                </defs>
                <g
                    transform={`translate(${position.x}, ${position.y}) scale(${scale})`}
                >
                    <MapSvg />

                    {!showDensityMap &&
                        calibrationPoints
                            .filter(
                                (point) =>
                                    point.capital &&
                                    filters.region === "all" &&
                                    point.region !== hiddenCapital &&
                                    getRegionDepositsCount(point.region) > 0,
                            )
                            .map((point) => {
                                const [x, y] = geoToSvg(
                                    point.geo[0],
                                    point.geo[1],
                                    point.region,
                                    point.federalDistrict,
                                );
                                const depositsCount = getRegionDepositsCount(
                                    point.region,
                                );
                                const circleSize = Math.max(
                                    window.innerWidth <= 768
                                        ? 400 / scale
                                        : 200 / scale,
                                    window.innerWidth <= 768 ? 150 : 50,
                                );
                                const fontSize = Math.max(
                                    window.innerWidth <= 768
                                        ? 400 / scale
                                        : 200 / scale,
                                    window.innerWidth <= 768 ? 150 : 50,
                                );

                                if (depositsCount === 0) return null;

                                return (
                                    <g
                                        key={`capital-${point.name}`}
                                        onClick={() =>
                                            handleCapitalClick(point)
                                        }
                                        style={{ cursor: "pointer" }}
                                        transform={`translate(${x}, ${y})`}
                                    >
                                        <circle
                                            r={circleSize}
                                            fill="#4B96DC"
                                            opacity="0.8"
                                            stroke="black"
                                            strokeWidth={20 / scale}
                                        />
                                        <text
                                            x="0"
                                            y="0"
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            fill="white"
                                            fontSize={fontSize}
                                            fontWeight="bold"
                                        >
                                            {depositsCount}
                                        </text>
                                        <title>{point.name}</title>
                                    </g>
                                );
                            })}

                    {showDensityMap
                        ? filteredPoints
                              .filter(
                                  (point) =>
                                      (point.group === "mining" ||
                                          point.group === "alkaline") &&
                                      Array.isArray(point.coordinates) &&
                                      point.coordinates.length === 2 &&
                                      typeof point.coordinates[0] ===
                                          "number" &&
                                      typeof point.coordinates[1] === "number",
                              )
                              .map((point) => {
                                  const [x, y] = geoToSvg(
                                      point.coordinates[0],
                                      point.coordinates[1],
                                      point.region,
                                      point.federalDistrict,
                                  );
                                  return (
                                      <circle
                                          key={`density-${point.id}`}
                                          cx={x}
                                          cy={y}
                                          r={60 / scale}
                                          fill={
                                              point.group === "mining"
                                                  ? DENSITY_COLORS.mining
                                                  : DENSITY_COLORS.alkaline
                                          }
                                          stroke="#fff"
                                          strokeWidth={2 / scale}
                                          onClick={() =>
                                              handlePointClick(point)
                                          }
                                          style={{ cursor: "pointer" }}
                                      >
                                          <title>{point.name}</title>
                                      </circle>
                                  );
                              })
                        : showRegionPoints &&
                          filteredPoints
                              .filter(
                                  (point) => point.region === showRegionPoints,
                              )
                              .reduce((acc, point) => {
                                  const [baseX, baseY] = geoToSvg(
                                      point.coordinates[0],
                                      point.coordinates[1],
                                      point.region,
                                      point.federalDistrict,
                                  );
                                  let x = baseX;
                                  let y = baseY;
                                  let attempts = 0;
                                  const radius = 150;
                                  while (attempts < 8) {
                                      const hasOverlap = acc.some(
                                          (placedPoint) =>
                                              checkOverlap(
                                                  x,
                                                  y,
                                                  placedPoint.x,
                                                  placedPoint.y,
                                              ),
                                      );
                                      if (!hasOverlap) break;
                                      const angle =
                                          (Math.PI * 2 * attempts) / 8;
                                      const candidateX =
                                          baseX + Math.cos(angle) * radius;
                                      const candidateY =
                                          baseY + Math.sin(angle) * radius;
                                      // Не даём точке при разведении
                                      // "убежать" за пределы своего региона.
                                      if (
                                          isPointInsideRegion(
                                              point.region,
                                              candidateX,
                                              candidateY,
                                          )
                                      ) {
                                          x = candidateX;
                                          y = candidateY;
                                      }
                                      attempts++;
                                  }
                                  acc.push({ point, x, y });
                                  return acc;
                              }, [])
                              .map(({ point, x, y }) => {
                                  const icon = getIconByWasteType(
                                      point.wasteType,
                                  );
                                  const iconSize = Math.max(
                                      BASE_ICON_SIZE / scale,
                                      MIN_ICON_SIZE,
                                  );
                                  const offset = iconSize / 2;
                                  return (
                                      <g
                                          key={point.id}
                                          transform={`translate(${
                                              x - offset
                                          }, ${y - offset})`}
                                          onClick={() =>
                                              handlePointClick(point)
                                          }
                                          style={{ cursor: "pointer" }}
                                      >
                                          <mask id={`circleMask-${point.id}`}>
                                              <circle
                                                  cx={iconSize / 2}
                                                  cy={iconSize / 2}
                                                  r={iconSize / 2}
                                                  fill="white"
                                              />
                                          </mask>
                                          <image
                                              href={icon}
                                              width={iconSize}
                                              height={iconSize}
                                              mask={`url(#circleMask-${point.id})`}
                                          >
                                              <title>{point.name}</title>
                                          </image>
                                      </g>
                                  );
                              })}
                </g>
            </svg>

            <div className="zoom-controls">
                <button className="zoom-button" onClick={handleZoomIn}>
                    +
                </button>
                <button className="zoom-button" onClick={handleZoomOut}>
                    -
                </button>
                {/* <button className="zoom-button" onClick={handleReset}>⟲</button> */}
                <button
                    className="zoom-button reset-all"
                    onClick={handleReset}
                    title="Сбросить все фильтры (ESC)"
                >
                    ⟲
                </button>
            </div>
            <ObjectLegend
                setShowLeftPanel={setShowLeftPanel}
                point={selectedPoint}
            />
        </div>
    );
};

export default SvgMap;
